/* Created by Interactive 63 - https://interactive63.com */
/**
 * Pure zoom and pan arithmetic for the stage viewport.
 */
import type { Vec2 } from '../../../core/model/types';

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 32;
export const FIT_PADDING = 40;

export interface Size {
  width: number;
  height: number;
}

export interface View {
  zoom: number;
  pan: Vec2;
}

/** Limits a zoom level to the supported range. */
export function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/** Centres content in the viewport at the given zoom. */
export function centreView(content: Size, viewport: Size, zoom: number): View {
  return {
    zoom,
    pan: {
      x: (viewport.width - content.width * zoom) / 2,
      y: (viewport.height - content.height * zoom) / 2,
    },
  };
}

/** Finds the zoom and pan that fit the content inside the viewport with padding on each side. */
export function fitView(content: Size, viewport: Size, padding = FIT_PADDING): View {
  const availableWidth = Math.max(1, viewport.width - padding * 2);
  const availableHeight = Math.max(1, viewport.height - padding * 2);
  const zoom = clampZoom(
    Math.min(availableWidth / content.width, availableHeight / content.height),
  );
  return centreView(content, viewport, zoom);
}

/** Changes zoom while keeping the content point under `anchor` (viewport pixels) fixed. */
export function zoomAt(view: View, newZoom: number, anchor: Vec2): View {
  const zoom = clampZoom(newZoom);
  const ratio = zoom / view.zoom;
  return {
    zoom,
    pan: {
      x: anchor.x - (anchor.x - view.pan.x) * ratio,
      y: anchor.y - (anchor.y - view.pan.y) * ratio,
    },
  };
}

/** The zoom multiplier for a wheel event: 1.1 per standard notch, proportional for small deltas. */
export function wheelZoomFactor(deltaY: number): number {
  return 1.1 ** (-Math.max(-100, Math.min(100, deltaY)) / 100);
}
